import { useTranslation } from 'react-i18next';
import { useModuleStore } from '../stores/moduleStore';
import { MenuItem } from '../types/MenuItem';
import { Paths } from '../routes/paths';
import { useAuthStore } from '../stores/authStore';
import { UserRole } from '../types/enums/UserRole';
import MaterialIcon from '../ui/components/common/MaterialIcon';
import { useAppBarNotificationStore } from '../stores/notification/appBarNotificationStore';
import { PageStatus } from '../types/enums/PageStatus';
import { Badge, Box } from '@chakra-ui/react';

export const useMenuUtils = () => {
  const { data: modules } = useModuleStore();
  const isAffiliate = useAuthStore(
    (state) => state.user?.role == UserRole.Affiliate
  );

  const { t } = useTranslation(['common', 'account', 'promotion', 'store']);
  const { status: notificationStatus, data: notificationData } =
    useAppBarNotificationStore();

  const allMenus = (): MenuItem[] => {
    let notificationCount = 0;
    if (notificationStatus == PageStatus.Loaded) {
      const notificationData2 = notificationData as Record<string, number>;
      if (notificationData2.totalCount) {
        notificationCount = notificationData2.totalCount;
      }
    }
    const menus: MenuItem[] = [];

    menus.push(
      new MenuItem({
        id: 'home',
        label: t('common:home'),
        link: Paths.Home,
        icon: <MaterialIcon iconName="home" size="inherit" />,
        activeIcon: <MaterialIcon iconName="home" fill={1} size="inherit" />,
        isBottom: true,
        hasChildren: true,
      })
    );
    menus.push(
      new MenuItem({
        id: 'referral',
        label: t('common:promoLinksShort'),
        title: t('promoLinks'),
        link: Paths.Referral,
        icon: <MaterialIcon iconName="link" size="inherit" />,
        isBottom: true,
        hasChildren: true,
      })
    );
    menus.push(
      new MenuItem({
        id: 'wallet',
        label: t('common:wallet'),
        link: Paths.Wallet,
        icon: <MaterialIcon iconName="account_balance_wallet" size="inherit" />,
        activeIcon: (
          <MaterialIcon
            iconName="account_balance_wallet"
            fill={1}
            size="inherit"
          />
        ),
        isBottom: true,
        hasChildren: true,
      })
    );

    if (isAffiliate) {
      menus.push(
        new MenuItem({
          id: 'network',
          label: t('common:network'),
          link: Paths.Network,
          icon: <MaterialIcon iconName="hub" size="inherit" />,
          activeIcon: <MaterialIcon iconName="hub" fill={1} size="inherit" />,
          isBottom: true,
          hasChildren: true,
        })
      );
    }

    menus.push(
      new MenuItem({
        id: 'account',
        label: t('common:account'),
        link: Paths.Account,
        icon: <MaterialIcon iconName="person" size="inherit" />,
        activeIcon: <MaterialIcon iconName="person" fill={1} size="inherit" />,
        isBottom: true,
      })
    );

    if (modules?.kyc) {
      menus.push(
        new MenuItem({
          id: 'kyc',
          label: t('common:kyc'),
          link: Paths.Kyc,
          icon: <MaterialIcon iconName="admin_panel_settings" size="inherit" />,
          activeIcon: (
            <MaterialIcon
              iconName="admin_panel_settings"
              fill={1}
              size="inherit"
            />
          ),
        })
      );
    }

    if (modules?.crm) {
      menus.push(
        new MenuItem({
          id: 'crm',
          label: t('promotion:crm'),
          link: Paths.Crm,
          icon: <MaterialIcon iconName="id_card" size="inherit" />,
          activeIcon: (
            <MaterialIcon iconName="id_card" fill={1} size="inherit" />
          ),
          hasChildren: true,
        })
      );
    }

    if (!modules?.store && modules?.packages) {
      menus.push(
        new MenuItem({
          id: 'orders',
          label: t('store:orders'),
          link: Paths.Order,
          icon: <MaterialIcon iconName="local_shipping" size="inherit" />,
          activeIcon: (
            <MaterialIcon iconName="local_shipping" fill={1} size="inherit" />
          ),
        })
      );
    }

    if (modules?.supportMessage) {
      menus.push(
        new MenuItem({
          id: 'mailbox',
          label: t('account:mailbox'),
          link: Paths.Mail,
          icon: <MaterialIcon iconName="mail" size="inherit" />,
          activeIcon: <MaterialIcon iconName="mail" fill={1} size="inherit" />,
          hasChildren: true,
        })
      );
    }

    menus.push(
      new MenuItem({
        id: 'notifications',
        label: t('account:notifications'),
        link: Paths.Notification,
        icon: (
          <Box pos="relative" display="inline-flex">
            <MaterialIcon iconName="notifications" size="inherit" />
            {notificationCount > 0 && (
              <Badge
                variant="solid"
                position="absolute"
                top="-4px"
                left="-8px"
                fontSize="11px"
                colorScheme="primary"
                borderRadius="full"
                px="2"
              >
                {notificationCount}
              </Badge>
            )}
          </Box>
        ),
        activeIcon: (
          <Box pos="relative" display="inline-flex">
            <MaterialIcon iconName="notifications" fill={1} size="inherit" />
            {notificationCount > 0 && (
              <Badge
                variant="solid"
                position="absolute"
                top="-4px"
                left="-8px"
                fontSize="11px"
                colorScheme="primary"
                borderRadius="full"
                px="2"
              >
                {notificationCount}
              </Badge>
            )}
          </Box>
        ),
        hasChildren: true,
      })
    );

    if (modules?.stories) {
      menus.push(
        new MenuItem({
          id: 'stories',
          label: t('common:stories'),
          link: Paths.Story,
          icon: <MaterialIcon iconName="article" size="inherit" />,
          activeIcon: (
            <MaterialIcon iconName="article" fill={1} size="inherit" />
          ),
          hasChildren: true,
        })
      );
    }

    if (modules?.events) {
      menus.push(
        new MenuItem({
          id: 'events',
          label: t('account:events'),
          link: Paths.Events,
          icon: <MaterialIcon iconName="event" size="inherit" />,
          activeIcon: <MaterialIcon iconName="event" fill={1} size="inherit" />,
          hasChildren: true,
        })
      );
    }

    menus.push(
      new MenuItem({
        id: 'faqs',
        label: t('account:faqs'),
        link: Paths.Faqs,
        icon: <MaterialIcon iconName="forum" size="inherit" />,
        activeIcon: <MaterialIcon iconName="forum" fill={1} size="inherit" />,
      })
    );

    menus.push(
      new MenuItem({
        id: 'support',
        label: t('account:support'),
        link: Paths.Support,
        icon: <MaterialIcon iconName="headphones" size="inherit" />,
        activeIcon: (
          <MaterialIcon iconName="headphones" fill={1} size="inherit" />
        ),
      })
    );

    return menus;
  };

  const bottomMenus = () =>
    allMenus()
      .filter((item) => item.isBottom)
      .slice(0, 5);

  return { allMenus, bottomMenus };
};

export const getMenuColor = (index: number): string => {
  const colors = [
    '#3BA99C',
    '#925E78',
    '#3F8EFC',
    '#FF9914',
    '#6F58C9',
    '#F05365',
  ];
  const selectedIndex = index % colors.length;
  return colors[selectedIndex];
};

export const homeRoutes = [
  Paths.HomeDashboard,
  Paths.HomeBinary,
  Paths.HomePackage,
  Paths.HomeRank,
];

export const referralRoutes = [
  Paths.ReferralSummary,
  Paths.ReferralRecruiter,
  Paths.ReferralEarner,
];

export const walletRoutes = [
  Paths.WalletStatement,
  Paths.WalletEarning,
  Paths.WalletPayout,
];

export const networkRoutes = [
  Paths.NetworkGenealogy,
  Paths.NetworkTabular,
  Paths.NetworkDownline,
];

export const storyRoutes = [Paths.StoryArticle, Paths.StoryVideo];

export const profileNotificationRoutes = [
  Paths.ProfilePushNotification,
  Paths.ProfileEmailNotification,
];

export const notificationRoutes = [Paths.Notification];

export const mailRoutes = [Paths.MailInbox, Paths.MailSent];

export const bottomRoutes = [
  ...homeRoutes,
  ...referralRoutes,
  ...walletRoutes,
  ...networkRoutes,
  Paths.Account,
];

export const tabRoutes = [
  ...homeRoutes,
  ...referralRoutes,
  ...walletRoutes,
  ...networkRoutes,
  ...storyRoutes,
  ...profileNotificationRoutes,
  ...notificationRoutes,
  ...mailRoutes,
];

export const defaultRoutes: Record<string, string> = {
  [Paths.Home]: Paths.HomeDashboard,
  [Paths.Referral]: Paths.ReferralSummary,
  [Paths.Wallet]: Paths.WalletStatement,
  [Paths.Network]: Paths.NetworkGenealogy,
  [Paths.Story]: Paths.StoryArticle,
  [Paths.ProfileNotification]: Paths.ProfileEmailNotification,
  [Paths.Mail]: Paths.MailInbox,
};
